import { useMemo } from "react";

import type { PayinAmountWithFeeLabels } from "@/shared/components/payments/PayinAmountWithFee";
import useTranslations from "@/shared/lang/useTranslations";

import { PayinRail } from "../services/paymentsPayin.service";

/** Rail-specific one-liner explaining how the money will actually arrive. */
export function usePayinRailHint(rail: PayinRail): string {
  const { t } = useTranslations("payins");
  switch (rail) {
    case PayinRail.BREB:
      return t("breb_form_hint");
    case PayinRail.PIX:
      return t("pix_form_hint");
    case PayinRail.SPEI:
      return t("spei_form_hint");
    case PayinRail.CBU_CVU:
      return t("cbu_cvu_form_hint");
    case PayinRail.PSE:
      return t("pse_form_hint");
    case PayinRail.NEQUI:
      return t("nequi_form_hint");
    case PayinRail.BANCOLOMBIA_BUTTON:
      return t("bancolombia_button_form_hint");
  }
}

/**
 * Copy for the shared amount+fee control, from the COLLECTOR's point of view (`/app/payins`): the user is
 * charging someone else, so the modes read "quiero cobrar" / "quiero recibir".
 *
 * `simulator` swaps the hints for the push-detected corridors, where nothing is created and the payer is
 * whoever transfers to the organization's standing account — "lo que te van a transferir", not "lo que vas a
 * cobrar".
 */
export function usePayinAmountLabels(variant: "form" | "simulator" = "form"): PayinAmountWithFeeLabels {
  const { t } = useTranslations("payins");
  return useMemo(
    () => ({
      modeGross: t("mode_gross_label"),
      modeNet: t("mode_net_label"),
      modeGrossHint: variant === "simulator" ? t("simulator_gross_hint") : t("mode_gross_hint"),
      modeNetHint: variant === "simulator" ? t("simulator_net_hint") : t("mode_net_hint"),
      inputGross: t("form_type_amount_sent"),
      inputNet: t("form_type_amount_received"),
      derivedNet: (vars) => t("derived_net_text", vars),
      derivedGross: (vars) => t("derived_gross_text", vars),
    }),
    [t, variant],
  );
}
